import React from 'react';
import { ChevronDown, ChevronUp, Loader2, Briefcase, MapPin, ArrowUpDown } from 'lucide-react';
import { Button } from '../ui/button';
import { jobsApi, getErrorMessage } from '../../services/api';
import { toast } from 'sonner';

// Same color ramps as ClientStageHeatmap so the two cards read as a set.
const STAGE_RAMPS = {
  sourced: ['bg-white', 'bg-slate-100', 'bg-slate-200', 'bg-slate-300', 'bg-slate-400 text-white', 'bg-slate-500 text-white'],
  screening: ['bg-white', 'bg-sky-50', 'bg-sky-100', 'bg-sky-200', 'bg-sky-300', 'bg-sky-400 text-white'],
  client_submission: ['bg-white', 'bg-violet-50', 'bg-violet-100', 'bg-violet-200', 'bg-violet-300', 'bg-violet-400 text-white'],
  interview_scheduled: ['bg-white', 'bg-amber-50', 'bg-amber-100', 'bg-amber-200', 'bg-amber-300', 'bg-amber-400 text-white'],
  interview_selected: ['bg-white', 'bg-lime-50', 'bg-lime-100', 'bg-lime-200', 'bg-lime-300', 'bg-lime-400 text-white'],
  offer_released: ['bg-white', 'bg-teal-50', 'bg-teal-100', 'bg-teal-200', 'bg-teal-300', 'bg-teal-400 text-white'],
  joined: ['bg-white', 'bg-emerald-50', 'bg-emerald-100', 'bg-emerald-200', 'bg-emerald-300', 'bg-emerald-500 text-white'],
  rejected: ['bg-white', 'bg-red-50', 'bg-red-100', 'bg-red-200', 'bg-red-300', 'bg-red-400 text-white'],
  dropped: ['bg-white', 'bg-orange-50', 'bg-orange-100', 'bg-orange-200', 'bg-orange-300', 'bg-orange-400 text-white'],
  dropped_out: ['bg-white', 'bg-rose-50', 'bg-rose-100', 'bg-rose-200', 'bg-rose-300', 'bg-rose-500 text-white'],
};

const PERIOD_TABS = [
  { key: 'all_time', label: 'All time' },
  { key: 'current_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'custom', label: 'Custom' },
];

const STATUS_TABS = [
  { key: 'open', label: 'Open jobs' },
  { key: 'all', label: 'All jobs' },
];

const intensityClass = (value, maxForStage, stageId) => {
  const ramp = STAGE_RAMPS[stageId] || STAGE_RAMPS.sourced;
  if (!value || maxForStage <= 0) return ramp[0];
  const ratio = value / maxForStage;
  const idx = Math.min(ramp.length - 1, Math.max(1, Math.ceil(ratio * (ramp.length - 1))));
  return ramp[idx];
};

/**
 * Per-Job × Pipeline-Stage table.
 *
 * Lives below the client-stage heatmap on /pipeline → Analytics. Lets DMs
 * spot single-job bottlenecks ("Senior FE Engineer · 12 submitted · 0
 * selected").
 *
 * Sortable by total or by any stage column.
 */
const JobStageTable = ({
  onCellClick,
  // Optional controlled props — when provided, hides the period/date pickers
  // and uses the parent-supplied values instead.
  period: controlledPeriod,
  startDate: controlledStart,
  endDate: controlledEnd,
}) => {
  const controlled = controlledPeriod !== undefined;
  const [collapsed, setCollapsed] = React.useState(false);
  const [localPeriod, setLocalPeriod] = React.useState('all_time');
  const [statusFilter, setStatusFilter] = React.useState('open');
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [localStart, setLocalStart] = React.useState(fmt(monthStart));
  const [localEnd, setLocalEnd] = React.useState(fmt(today));
  const period = controlled ? controlledPeriod : localPeriod;
  const startDate = controlled ? (controlledStart || fmt(monthStart)) : localStart;
  const endDate = controlled ? (controlledEnd || fmt(today)) : localEnd;
  const setPeriod = controlled ? () => {} : setLocalPeriod;
  const setStartDate = controlled ? () => {} : setLocalStart;
  const setEndDate = controlled ? () => {} : setLocalEnd;
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  // Sort: column key ('total' or stage id) + direction ('desc' / 'asc')
  const [sortBy, setSortBy] = React.useState('total');
  const [sortDir, setSortDir] = React.useState('desc');

  const load = React.useCallback(async (p, range, status) => {
    setLoading(true);
    try {
      const res = await jobsApi.jobStageMatrix(p, range, status);
      setData(res.data || null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load job analysis'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (collapsed) return;
    if (period === 'custom') {
      if (startDate && endDate && startDate <= endDate) {
        load('custom', { start_date: startDate, end_date: endDate }, statusFilter);
      }
    } else {
      load(period, null, statusFilter);
    }
  }, [period, startDate, endDate, statusFilter, collapsed, load]);

  const stages = data?.stages || [];
  const rows = data?.rows || [];
  const colTotals = data?.col_totals || {};
  const grand = data?.grand_total || 0;

  // Per-stage column max for intensity scaling.
  const maxByStage = React.useMemo(() => {
    const m = {};
    stages.forEach((s) => {
      let mx = 0;
      rows.forEach((r) => {
        const v = r.counts?.[s.id] || 0;
        if (v > mx) mx = v;
      });
      m[s.id] = mx;
    });
    return m;
  }, [stages, rows]);

  const sortedRows = React.useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = sortBy === 'total' ? a.total : a.counts?.[sortBy] || 0;
      const bv = sortBy === 'total' ? b.total : b.counts?.[sortBy] || 0;
      if (av === bv) return a.title.localeCompare(b.title);
      return (av - bv) * dir;
    });
  }, [rows, sortBy, sortDir]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const sortIcon = (key) =>
    sortBy === key ? (
      sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-0.5" /> : <ChevronDown className="w-3 h-3 inline ml-0.5" />
    ) : (
      <ArrowUpDown className="w-3 h-3 inline ml-0.5 opacity-40" />
    );

  const isEmpty = !loading && rows.length === 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="job-stage-table">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Briefcase className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-slate-800">Job × Pipeline Stage</h3>
          {data ? (
            <span className="text-xs text-slate-500 truncate">
              · {rows.length} jobs · {grand} applications
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!collapsed && (
            <>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="js-status-tabs">
                {STATUS_TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setStatusFilter(t.key)}
                    className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                      statusFilter === t.key
                        ? 'bg-teal-700 text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`js-status-${t.key}`}
                    title={t.key === 'open' ? 'Only Published / Assigned / On hold' : 'Include closed & draft too'}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {!controlled && (
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="js-period-tabs">
                  {PERIOD_TABS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setPeriod(t.key)}
                      className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                        period === t.key
                          ? 'bg-slate-900 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid={`js-period-${t.key}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed((v) => !v)}
            className="h-8 px-2"
            data-testid="js-toggle"
          >
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {!collapsed && !controlled && period === 'custom' && (
        <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50/60" data-testid="js-custom-range">
          <span className="text-xs font-medium text-slate-600">From</span>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-teal-500 outline-none"
            data-testid="js-start-date"
          />
          <span className="text-xs font-medium text-slate-600">to</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-teal-500 outline-none"
            data-testid="js-end-date"
          />
          {startDate && endDate && startDate > endDate ? (
            <span className="text-xs text-red-600 font-medium">Start must be ≤ End</span>
          ) : null}
        </div>
      )}

      {!collapsed && (
        <div className="p-3 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading job analysis…
            </div>
          ) : isEmpty ? (
            <div className="text-center py-10 text-slate-400 text-sm" data-testid="js-empty">
              No applications match the current filters. Try widening the period or switching to "All jobs".
            </div>
          ) : (
            <table className="text-xs border-collapse w-full" data-testid="js-table">
              <thead>
                <tr>
                  <th className="text-left text-[10px] uppercase tracking-wide text-slate-500 px-2 py-1.5 sticky left-0 bg-white z-10 min-w-[240px]">
                    Job
                  </th>
                  {stages.map((s) => (
                    <th
                      key={s.id}
                      className="text-center text-[10px] uppercase tracking-wide text-slate-500 px-2 py-1.5 min-w-[78px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort(s.id)}
                      data-testid={`js-col-${s.id}`}
                      title={`Sort by ${s.label}`}
                    >
                      <div className="truncate max-w-[88px]">{s.label}{sortIcon(s.id)}</div>
                    </th>
                  ))}
                  <th
                    className="text-center text-[10px] uppercase tracking-wide text-slate-700 font-bold px-2 py-1.5 bg-slate-50 min-w-[60px] cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('total')}
                    title="Sort by total applications"
                  >
                    Total{sortIcon('total')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <tr key={r.job_id} data-testid={`js-row-${r.job_id}`}>
                    <td
                      className="text-left text-slate-700 px-2 py-1.5 sticky left-0 bg-white z-10 max-w-[260px]"
                      title={`${r.title} · ${r.client_name}${r.location ? ' · ' + r.location : ''}`}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-medium truncate">{r.title}</span>
                        {r.location ? (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-600 bg-slate-100 border border-slate-200 rounded px-1 py-0 flex-shrink-0">
                            <MapPin className="w-2.5 h-2.5" />
                            {r.location}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {r.client_name}{r.vacancies ? ` · ${r.vacancies} vac` : ''}
                      </div>
                    </td>
                    {stages.map((s) => {
                      const v = r.counts?.[s.id] || 0;
                      const isClickable = v > 0 && typeof onCellClick === 'function';
                      return (
                        <td
                          key={`${r.job_id}-${s.id}`}
                          className={`text-center px-2 py-1.5 border border-slate-100 font-semibold text-slate-800 ${intensityClass(v, maxByStage[s.id] || 0, s.id)} ${isClickable ? 'cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-slate-400 transition-shadow' : ''}`}
                          data-testid={`js-cell-${r.job_id}-${s.id}`}
                          title={`${r.title} · ${s.label}: ${v}`}
                          onClick={isClickable ? () => onCellClick({ jobId: r.job_id, jobTitle: r.title, stage: s.id, stageLabel: s.label, value: v }) : undefined}
                        >
                          {v || ''}
                        </td>
                      );
                    })}
                    <td
                      className="text-center px-2 py-1.5 font-bold text-slate-900 bg-slate-50"
                      data-testid={`js-row-total-${r.job_id}`}
                    >
                      {r.total}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td className="text-left text-slate-700 font-bold px-2 py-1.5 sticky left-0 bg-slate-50 z-10">
                    Total
                  </td>
                  {stages.map((s) => (
                    <td
                      key={`total-${s.id}`}
                      className="text-center px-2 py-1.5 font-bold text-slate-900"
                      data-testid={`js-col-total-${s.id}`}
                    >
                      {colTotals[s.id] || 0}
                    </td>
                  ))}
                  <td
                    className="text-center px-2 py-1.5 font-bold text-slate-900"
                    data-testid="js-grand-total"
                  >
                    {grand}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default JobStageTable;
